import React, { ChangeEvent, useState } from "react";
import { Steps } from "antd";
import DatasetsSources from "../datasets/datasets-sources/DatasetsSources";
import SearchInput from "../datasets/datasets-tab-content/SearchInput";
import { styled } from "styled-components";
import {
  Authority,
  DataSourceType,
  IDatabaseRequest,
  IEnterpriseConnectorRequest,
  IFileRequest,
  isEmpty,
  useDebounce,
  useMountedEffect,
} from "@dataone/lib";
import {
  IDataSourceApp,
  joinedDataSourceSelection,
} from "../datasets/datasets-sources/DatasetsSources.config";
import { LoadingOutlined } from "@ant-design/icons";
import { FinalStep } from "./FinalStep";
import { UseQueryResult } from "@tanstack/react-query";
import UpdateDatabaseForm from "../datasets/datasets-forms/UpdateDatabaseForm";
import UpdateFileForm from "../datasets/datasets-forms/UpdateFileForm";
import UpdateEnterpriseConnectorForm from "../datasets/datasets-forms/UpdateEnterpriseConnectorForm";
import { useAuth } from "../../../auth/contexts/auth.provider";

interface StepsProps {
  current: number;
  iLoading: boolean;
  failed: boolean;
  dataSource: IDataSourceApp | null;
  formMode: "update" | "create";
  dataSourceData:
    | IDatabaseRequest
    | IFileRequest
    | IEnterpriseConnectorRequest
    | null;
  setDataSource: React.Dispatch<React.SetStateAction<IDataSourceApp | null>>;
  setCurrent: React.Dispatch<React.SetStateAction<number>>;
  setILoading: React.Dispatch<React.SetStateAction<boolean>>;
  setFailed: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateSuccess: () => void;
  refetchDatasets?: (options?: {
    throwOnError: boolean;
    cancelRefetch: boolean;
  }) => Promise<UseQueryResult>;
}

const DataSourceStepsForm = ({
  current,
  iLoading,
  failed,
  formMode,
  dataSource,
  dataSourceData,
  setILoading,
  setFailed,
  setDataSource,
  setCurrent,
  onCreateSuccess,
  refetchDatasets,
}: StepsProps) => {
  const [isSearchLoading, setSearchLoading] = useState(false);
  const [searchDataset, setSearchDataset] = useState("");
  const [filteredDatasets, setFilteredDatasets] = useState<IDataSourceApp[]>(
    []
  );

  const debouncedSearch = useDebounce(searchDataset);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchLoading(true);
    setSearchDataset(value);
    if (value === debouncedSearch) setSearchLoading(false);
  };

  const { user } = useAuth();
  const hasDatabases =
    ((user?.groups[0] === Authority.OG_SUBSCRIBER ||
      user?.groups[0] === Authority.OG_SUBSCRIBER_EX) &&
      user?.subscription.hasDatabases === true) ||
    user?.groups[0] === Authority.OG_ADMIN;
  const hasFiles =
    ((user?.groups[0] === Authority.OG_SUBSCRIBER ||
      user?.groups[0] === Authority.OG_SUBSCRIBER_EX) &&
      user?.subscription.hasFiles === true) ||
    user?.groups[0] === Authority.OG_ADMIN;
  const hasEnterpriseConnectors =
    ((user?.groups[0] === Authority.OG_SUBSCRIBER ||
      user?.groups[0] === Authority.OG_SUBSCRIBER_EX) &&
      user?.subscription.hasEnterpriseConnectors === true) ||
    user?.groups[0] === Authority.OG_ADMIN;
  const hasCloudStorage =
    ((user?.groups[0] === Authority.OG_SUBSCRIBER ||
      user?.groups[0] === Authority.OG_SUBSCRIBER_EX) &&
      user?.subscription.hasCloudStorage === true) ||
    user?.groups[0] === Authority.OG_ADMIN;

  // useMountedEffect(() => {
  //   if (!isEmpty(debouncedSearch))
  //     setFilteredDatasets(
  //       joinedDataSourceSelection.filter((app) =>
  //         app.title
  //           .toLowerCase()
  //           .includes(debouncedSearch?.trim().toLowerCase() ?? "")
  //       )
  //     );
  //   else setFilteredDatasets([]);
  //   setSearchLoading(false);
  // }, [debouncedSearch]);
  useMountedEffect(() => {
    if (!isEmpty(debouncedSearch)) {
      setFilteredDatasets(
        joinedDataSourceSelection.filter(
          (app) =>
            debouncedSearch &&
            app.title
              .toLowerCase()
              .includes(debouncedSearch.trim().toLowerCase()) &&
            ((app.parentType === DataSourceType.DATABASE && hasDatabases) ||
              (app.parentType === DataSourceType.FILE && hasFiles) ||
              (app.parentType === DataSourceType.ENTERPRISE_CONNECTOR &&
                hasEnterpriseConnectors) ||
              (app.parentType === DataSourceType.CLOUD_STORAGE &&
                hasCloudStorage))
        )
      );
    } else {
      setFilteredDatasets([]);
    }
    setSearchLoading(false);
  }, [
    debouncedSearch,
    hasDatabases,
    hasFiles,
    hasEnterpriseConnectors,
    hasCloudStorage,
  ]);

  let items = [
    {
      title: "Type Selection",
    },
    {
      title: "Configuration",
      icon: iLoading ? <LoadingOutlined /> : null,
    },
    {
      title: "Done",
    },
  ];

  return (
    <StyledSteps $formMode={formMode}>
      <Steps current={current} items={items} />
      {current === 0 ? (
        <>
          <SearchWrapper>
            <SearchInput
              isSearchLoading={isSearchLoading}
              handleSearchChange={handleSearchChange}
              placeholder="Search by name/type..."
            />
          </SearchWrapper>
          <StepContent $maxHeight="350px">
            <DatasetsSources
              filteredDatasets={filteredDatasets}
              setFilteredDatasets={setFilteredDatasets}
              setDataSource={setDataSource}
              setCurrent={setCurrent}
            />
          </StepContent>
        </>
      ) : current === 1 ? (
        <div>
          {dataSource && dataSource.parentType === DataSourceType.DATABASE && (
            <UpdateDatabaseForm
              formMode={formMode}
              failed={failed}
              dataSource={dataSource}
              databaseData={dataSourceData as IDatabaseRequest}
              setILoading={setILoading}
              setCurrent={setCurrent}
              setFailed={setFailed}
              onCreateSuccess={onCreateSuccess}
              refetchDatasets={refetchDatasets}
            />
          )}
          {dataSource && dataSource.parentType === DataSourceType.FILE && (
            <UpdateFileForm
              formMode={formMode}
              failed={failed}
              dataSource={dataSource}
              fileData={dataSourceData as IFileRequest}
              setILoading={setILoading}
              setCurrent={setCurrent}
              setFailed={setFailed}
              onCreateSuccess={onCreateSuccess}
              refetchDatasets={refetchDatasets}
            />
          )}
          {dataSource &&
            dataSource.parentType === DataSourceType.ENTERPRISE_CONNECTOR && (
              <UpdateEnterpriseConnectorForm
                formMode={formMode}
                failed={failed}
                dataSource={dataSource}
                enterpriseConnectorData={
                  dataSourceData as IEnterpriseConnectorRequest
                }
                setILoading={setILoading}
                setCurrent={setCurrent}
                setFailed={setFailed}
                onCreateSuccess={onCreateSuccess}
                refetchDatasets={refetchDatasets}
              />
            )}
          {/* {failed && <ErrorStep />} */}
        </div>
      ) : (
        <>
          <FinalStep formMode={formMode} />
        </>
      )}
    </StyledSteps>
  );
};

export default DataSourceStepsForm;

const StyledSteps = styled.div<{ $formMode: string }>`
  .ant-steps {
    display: ${(props) => (props.$formMode === "update" ? "none" : "flex")};
    margin-top: -5px;
  }
  .ant-steps-item-icon {
    width: 25px;
    height: 25px;
    line-height: 25px;
  }
  .ant-steps-item-title {
    line-height: 30px;
    font-size: 14px;
  }
`;
const SearchWrapper = styled.div`
  .search-container {
    margin-inline: auto;
    margin-top: 30px;
  }
`;

const StepContent = styled.div<{ $maxHeight: string }>`
  max-height: ${(p) => p.$maxHeight};
  overflow-y: auto;
  margin-top: 5px;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #fff;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #8b8b8b;
    border-radius: 7px;
  }

  &::-webkit-scrollbar-button {
    display: block;
    height: 16px;
    width: 16px;
    background-color: #fff;
    border: none;
  }

  &::-webkit-scrollbar-button:single-button:vertical:decrement {
    height: 12px;
    width: 16px;
    background-position: center 6px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='rgb(96, 96, 96)'><polygon points='50,00 0,50 100,50'/></svg>");
  }
  &::-webkit-scrollbar-button:single-button:vertical:increment {
    height: 12px;
    width: 16px;
    background-position: center center;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='rgb(96, 96, 96)'><polygon points='0,0 100,0 50,50'/></svg>");
  }
`;
